import React from 'react';
import axios from 'axios';

import { ApiResult, FlowExecution, FlowSchedule, Project } from '../common/dataTypes/Jetstream';
import { Breadcrumb } from '../jet-ui/Breadcrumb/Breadcrumb';
import { Content } from '../jet-ui/Content/Content';
import { Card } from '../jet-ui/Card/Card';
import { UpcomingTable } from './UpcomingTable';

import './Monitor.css';
import { RunningTable } from './RunningTable';
import { RecentTable } from './RecentTable';
import { useAuth } from '../common/auth/use-auth';
import { useArrayQueryParam, useStringQueryParam } from '../common/hooks/useQueryFilters';
import { useDisplayPreferences } from '../common/hooks/useDisplayPreferences';

export const Monitor: React.FC = () => {

  const [projectFilter, setProjectFilter] = useArrayQueryParam('project');
  const [flowFilter, setFlowFilter] = useArrayQueryParam('flow');
  const [userFilter, setUserFilter] = useArrayQueryParam('user');
  const [statusFilter, setStatusFilter] = useArrayQueryParam('status');
  const [projects, setProjects] = React.useState<Project[]>([]);

  const [startedFrom, setStartedFrom] = useStringQueryParam('startedFrom');
  const [startedTo, setStartedTo] = useStringQueryParam('startedTo');
  const [endedFrom, setEndedFrom] = useStringQueryParam('endedFrom');
  const [endedTo, setEndedTo] = useStringQueryParam('endedTo');

  const [upcoming, setUpcoming] = React.useState<any[]>([]);
  const [upcomingLoading, setUpcomingLoading] = React.useState(true);
  const [flowProjectMap, setFlowProjectMap] = React.useState<Record<string, string | null>>({});

  const [running, setRunning] = React.useState<FlowExecution[]>([]);
  const [runningLoading, setRunningLoading] = React.useState(true);

  const [recent, setRecent] = React.useState<FlowExecution[]>([]);
  const [recentPages, setRecentPages] = React.useState(0);
  const { prefs } = useDisplayPreferences();
  const recentRowsPerPage = prefs.monitorRowsPerPage;
  const [recentPageIndex, setRecentPageIndex] = React.useState(0);
  const [recentLoading, setRecentLoading] = React.useState(true);

  // Derived: distinct flow names and user names from recent data for filter options
  const [allFlowNames, setAllFlowNames] = React.useState<string[]>([]);
  const [allUserNames, setAllUserNames] = React.useState<string[]>([]);
  const [allStatusNames, setAllStatusNames] = React.useState<string[]>([]);

  const auth = useAuth();

  // Fetch projects list
  React.useEffect(() => {
    axios.get<ApiResult<Project[]>>('/api/projects', auth.getRequestConfig())
      .then(res => setProjects(res.data.data))
      .catch(() => {});
  }, [auth.user]);

  // Fetch flow-to-project mapping for permission checks
  React.useEffect(() => {
    axios.get<ApiResult<any>>('/api/flows', auth.getRequestConfig())
      .then(res => {
        const flows: { name: string; project?: string }[] = res.data.data.flows;
        const map: Record<string, string | null> = {};
        flows.forEach(f => { map[f.name] = f.project ?? null; });
        setFlowProjectMap(map);
      })
      .catch(() => {});
  }, [auth.user]);

  // Fetch distinct flow names, users, and statuses for filter dropdowns
  // Re-fetch flow names when projectFilter changes to scope options
  React.useEffect(() => {
    let url = '/api/executions/recent?itemsPerPage=1000&pageNumber=0';
    projectFilter.forEach(p => { url += `&projectName=${encodeURIComponent(p)}`; });
    axios.get<ApiResult<any>>(url, auth.getRequestConfig())
      .then(res => {
        const executions: FlowExecution[] = res.data.data.executions;
        const availableFlows = Array.from(new Set(executions.map(e => e.flowName))).sort();
        setAllFlowNames(availableFlows);
        // Clear flow filter selections that are no longer available
        setFlowFilter(prev => prev.filter(f => availableFlows.includes(f)));
        setAllUserNames(Array.from(new Set(executions.map(e => e.userId))).sort());
        setAllStatusNames(Array.from(new Set(executions.map(e => e.status))).sort());
      })
      .catch(() => {});
  }, [auth.user, projectFilter]);

  const buildProjectParams = () => projectFilter.map(p => `&projectName=${encodeURIComponent(p)}`).join('');

  const fetchUpcoming = React.useCallback(async () => {
    const result = await axios.get<ApiResult<any[]>>(`/api/executions/scheduled?_=1${buildProjectParams()}`, auth.getRequestConfig());
    setUpcoming(result.data.data);
    setUpcomingLoading(false);
  }, [projectFilter]);

  const fetchRunning = React.useCallback(async () => {
    const result = await axios.get<ApiResult<any>>(`/api/executions?_=1${buildProjectParams()}`, auth.getRequestConfig());
    setRunning(result.data.data);
    setRunningLoading(false);
  }, [projectFilter]);

  const fetchRecent = React.useCallback(async (rowsPerPage: number, pageIndex: number) => {
    let url = `/api/executions/recent?itemsPerPage=${rowsPerPage}&pageNumber=${pageIndex}`;
    projectFilter.forEach(p => { url += `&projectName=${encodeURIComponent(p)}`; });
    flowFilter.forEach(f => { url += `&flowName=${encodeURIComponent(f)}`; });
    userFilter.forEach(u => { url += `&userId=${encodeURIComponent(u)}`; });
    statusFilter.forEach(s => { url += `&status=${encodeURIComponent(s)}`; });
    if (startedFrom) url += `&startedFrom=${encodeURIComponent(startedFrom)}`;
    if (startedTo) url += `&startedTo=${encodeURIComponent(startedTo)}`;
    if (endedFrom) url += `&endedFrom=${encodeURIComponent(endedFrom)}`;
    if (endedTo) url += `&endedTo=${encodeURIComponent(endedTo)}`;
    const result = await axios.get<ApiResult<any>>(url, auth.getRequestConfig());
    setRecent(result.data.data.executions);
    setRecentPages(result.data.data.pages);
    setRecentLoading(false);
  }, [projectFilter, flowFilter, userFilter, statusFilter, startedFrom, startedTo, endedFrom, endedTo]);

  React.useEffect(() => {
    fetchUpcoming();
    fetchRunning();
    fetchRecent(recentRowsPerPage, recentPageIndex);

    const interval = setInterval(() => {
      fetchUpcoming();
      fetchRunning();
      fetchRecent(recentRowsPerPage, recentPageIndex);
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchRecent, fetchRunning, fetchUpcoming, recentPageIndex]);

  const onRecentPageChange = React.useCallback((pageIndex: number) => {
    setRecentLoading(true);
    setRecentPageIndex(pageIndex);
  }, []);

  const handleRun = React.useCallback(async (flowName: string) => {
    const config = auth.getRequestConfig();
    if (config) {
      config.headers = {...config.headers, 'Content-Type': 'application/json'};
    }
    await axios.post<ApiResult<{flowExecutionId: number}>>('/api/flows/run',
      JSON.stringify(flowName), config);
    await fetchRunning();
  }, [fetchRunning]);

  const handleOnRemove = React.useCallback(async (flowName: string) => {
    const noSchedule: FlowSchedule = {
      flowName: flowName,
      schedule: "",
      type: 'None'
    };
    await axios.post<ApiResult<any>>(`/api/flows/${encodeURIComponent(flowName)}/schedule`, noSchedule, auth.getRequestConfig());
    await fetchUpcoming();
  }, [fetchUpcoming]);

  const handleStop = React.useCallback(async (flowExecutionId: number) => {
    await axios.post<ApiResult<any>>('/api/executions/stop', JSON.stringify(flowExecutionId), auth.getRequestConfig({'Content-Type': 'application/json'}));
    await fetchRunning();
  }, [fetchRunning]);

  /** Check if the current user can run a flow by name. Returns { canRun, projectName }. */
  const getFlowRunPermission = React.useCallback((flowName: string): { canRun: boolean; projectName: string | null } => {
    const projectName = flowProjectMap[flowName] ?? null;
    if (!projectName) return { canRun: true, projectName: null };
    const project = projects.find(p => p.name === projectName);
    if (!project) return { canRun: true, projectName };
    return { canRun: project.canRun, projectName };
  }, [flowProjectMap, projects]);

  return (
    <Content className="Monitor">
      <div>
        <Breadcrumb href="/">Home</Breadcrumb>
        <Breadcrumb href="/monitor">Monitor</Breadcrumb>
      </div>
      <h1>Monitor</h1>
      <Card>
        <div className="Monitor__Section">
          <h2>Upcoming</h2>
          <UpcomingTable key="upcoming" data={upcoming} onRun={handleRun} onRemove={handleOnRemove} loading={upcomingLoading} getFlowRunPermission={getFlowRunPermission} />
        </div>
        <div className="Monitor__Section">
          <h2>Running</h2>
          <RunningTable key="running" data={running} onStop={handleStop} loading={runningLoading} />
        </div>
        <div className="Monitor__Section">
          <h2>Recent</h2>
          (displaying {recent.length} results)
          <RecentTable key="recent" data={recent} rowsPerPage={recentRowsPerPage} loading={recentLoading} pages={recentPages} onPageChange={onRecentPageChange} pageIndex={recentPageIndex}
            projects={projects}
            projectFilter={projectFilter}
            onProjectFilterChange={setProjectFilter}
            flowNames={allFlowNames}
            flowFilter={flowFilter}
            onFlowFilterChange={setFlowFilter}
            userNames={allUserNames}
            userFilter={userFilter}
            onUserFilterChange={setUserFilter}
            statusNames={allStatusNames}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            startedFrom={startedFrom}
            startedTo={startedTo}
            onStartedChange={(from, to) => { setStartedFrom(from); setStartedTo(to); }}
            endedFrom={endedFrom}
            endedTo={endedTo}
            onEndedChange={(from, to) => { setEndedFrom(from); setEndedTo(to); }}
          />
        </div>
      </Card>
    </Content>
  );
}
